import {
  Box,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { UnfoldMore, KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';

import './CompetitorStationsTable.css';
import { CompetitorStationsTableProps } from './CompetitorStationsTable.types';

export function CompetitorStationsTable({
  searchQuery,
  selectedStation,
  sortField,
  sortDirection,
  sortedStations,
  addingMode = false,
  filters,
  selectedFilters,
  onSort,
  onStationSelect,
  onSearchChange,
  onAddCompetitor,

  onFilterChange
}: CompetitorStationsTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getSortIcon = (column: string) => {
    if (sortField !== column) return <UnfoldMore />;
    return sortDirection === 'asc' ? <KeyboardArrowUp /> : <KeyboardArrowDown />;
  };

  const handleRowClick = (station: any) => {
    if (addingMode && onAddCompetitor) {
      onAddCompetitor(station);
    } else {
      onStationSelect(station);
    }
  };

  return (
    <Box className="competitor-stations-table-container">
      <Card className="competitor-stations-card">
        <CardHeader title="АЗК конкурентів" className="competitor-stations-card-header" />
        <CardContent className="competitor-stations-card-content">
          <Box className="competitor-stations-search-container">
            <TextField
              placeholder="Пошук"
              value={searchQuery}
              onChange={e => onSearchChange(e.target.value)}
              size="small"
              fullWidth
              className="competitor-stations-search-input"
            />
          </Box>

          <Box className="competitor-stations-filters-container">
            <Box className="competitor-stations-filters-row">
              <FormControl size="small" className="competitor-stations-filter-control">
                <InputLabel className="competitor-stations-filter-label">Регіон</InputLabel>
                <Select
                  value={selectedFilters?.region || 'Всі'}
                  onChange={e => onFilterChange('region', e.target.value)}
                  label="Регіон"
                  className="competitor-stations-filter-select"
                >
                  {filters.regions.map((region: any) => (
                    <MenuItem key={region} value={region}>
                      {region}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" className="competitor-stations-filter-control">
                <InputLabel className="competitor-stations-filter-label">Область</InputLabel>
                <Select
                  value={selectedFilters?.province || 'Всі'}
                  onChange={e => onFilterChange('province', e.target.value)}
                  label="Область"
                  className="competitor-stations-filter-select"
                >
                  {filters.province.map((prov: any) => (
                    <MenuItem key={prov} value={prov}>
                      {prov}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" className="competitor-stations-filter-control">
                <InputLabel className="competitor-stations-filter-label">Бренди</InputLabel>
                <Select
                  value={selectedFilters?.brand || 'Всі'}
                  onChange={e => onFilterChange('brand', e.target.value)}
                  label="Бренди"
                  className="competitor-stations-filter-select"
                >
                  {filters.brands.map((brand: any) => (
                    <MenuItem key={brand} value={brand}>
                      {brand}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {!isMobile && (
            <div className="competitor-stations-desktop-header">
              <div
                className="competitor-stations-desktop-header-cell comp-h-col-12 sortable"
                onClick={() => onSort('station_number')}
              >
                №<div className="comp-sort-icon">{getSortIcon('station_number')}</div>
              </div>
              <div
                className="competitor-stations-desktop-header-cell comp-h-col-35 sortable"
                onClick={() => onSort('address')}
              >
                Адреса
                <div className="comp-sort-icon">{getSortIcon('address')}</div>
              </div>
              <div
                className="competitor-stations-desktop-header-cell comp-h-col-18 sortable"
                onClick={() => onSort('region')}
              >
                Регіон
                <div className="comp-sort-icon">{getSortIcon('region')}</div>
              </div>
              <div
                className="competitor-stations-desktop-header-cell comp-h-col-17 sortable"
                onClick={() => onSort('province')}
              >
                Область
                <div className="comp-sort-icon">{getSortIcon('province')}</div>
              </div>
              <div
                className="competitor-stations-desktop-header-cell comp-h-col-20 sortable"
                onClick={() => onSort('brand')}
              >
                Бренд
                <div className="comp-sort-icon">{getSortIcon('brand')}</div>
              </div>
            </div>
          )}

          <Box className="competitor-stations-table">
            <div className="competitor-table-wrapper">
              <div className="competitor-stations-results-table">
                {isMobile && (
                  <div className="competitor-stations-table-header">
                    <div className="competitor-stations-table-header-row">
                      <div
                        className="competitor-stations-table-header-cell comp-col-12 sortable"
                        onClick={() => onSort('station_number')}
                      >
                        №<div className="comp-sort-icon">{getSortIcon('station_number')}</div>
                      </div>
                      <div
                        className="competitor-stations-table-header-cell comp-col-35 sortable"
                        onClick={() => onSort('address')}
                      >
                        Адреса
                        <div className="comp-sort-icon">{getSortIcon('address')}</div>
                      </div>
                      <div
                        className="competitor-stations-table-header-cell comp-col-18 sortable"
                        onClick={() => onSort('region')}
                      >
                        Регіон
                        <div className="comp-sort-icon">{getSortIcon('region')}</div>
                      </div>
                      <div
                        className="competitor-stations-table-header-cell comp-col-17 sortable"
                        onClick={() => onSort('province')}
                      >
                        Область
                        <div className="comp-sort-icon">{getSortIcon('province')}</div>
                      </div>
                      <div
                        className="competitor-stations-table-header-cell comp-col-20 sortable"
                        onClick={() => onSort('brand')}
                      >
                        Бренд
                        <div className="comp-sort-icon">{getSortIcon('brand')}</div>
                      </div>
                    </div>
                  </div>
                )}
                {sortedStations.map(station => (
                  <div
                    key={station.unicode}
                    onClick={() => handleRowClick(station)}
                    className={`competitor-stations-table-row ${selectedStation?.unicode === station.unicode ? 'selected-competitor' : ''}`}
                  >
                    <div className="competitor-stations-table-cell comp-col-12">{station.station_number}</div>
                    <div className="competitor-stations-table-cell comp-col-35">{station.address}</div>
                    <div className="competitor-stations-table-cell comp-col-18">{station.region}</div>
                    <div className="competitor-stations-table-cell comp-col-17">{station.province}</div>
                    <div className="competitor-stations-table-cell comp-col-20">{station.brand}</div>
                  </div>
                ))}
              </div>
            </div>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
